import { useContext, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { CheckCircle } from "lucide-react"
import { AuthLayout, BrandLogo, Button, SignUpForm } from "@/components"
import TurnstileWidget from "../ui/TurnstileWidget"
import { TURNSTILE_SITE_KEY, WEBSITE_URL } from "../config/index.js"
import { api } from "../system/api.js"
import { ThemeContext } from "../providers/ThemeProvider.jsx"
import SMLogo from "../assets/svg/sm-logo.svg?react"
import SMLogoWhite from "../assets/svg/sm-logo_white.svg?react"

export default function SwitchMarketSignUp() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { theme } = useContext(ThemeContext)
  const isDark = theme === "dark"
  const SwitchMarketsLogo = isDark ? SMLogoWhite : SMLogo

  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [formError, setFormError] = useState(null)
  const [turnstileToken, setTurnstileToken] = useState("")
  const [captchaReady, setCaptchaReady] = useState(false)
  const [success, setSuccess] = useState(false)

  const validate = ({ name, email, password, termsAccepted }) => {
    const next = {}
    if (!name.trim()) next.name = "Name is required"
    if (!email.trim()) next.email = "Email is required"
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = "Enter a valid email"
    if (!password) next.password = "Password is required"
    else if (password.length < 8) next.password = "Must be at least 8 characters"
    if (!termsAccepted) next.terms = "You must accept the terms"
    return next
  }

  const handleSubmit = async (values) => {
    if (loading) return

    const next = validate(values)
    setErrors(next)
    setFormError(null)
    if (Object.keys(next).length > 0) return

    if (!turnstileToken) {
      setFormError("Please complete the security verification.")
      return
    }

    setLoading(true)
    try {
      await api("sign/up/sponsor", {
        name: values.name,
        email: values.email,
        password: values.password,
        sponsor: "SwitchMarkets",
        turnstileToken,
      })
      setLoading(false)
      setSuccess(true)
      setTimeout(() => navigate("/"), 5000)
    } catch (err) {
      setLoading(false)
      const code = err?.errorCode
      if (code === "EMAIL_ALREADY_EXISTS") {
        setFormError(
          <span>
            It looks like you already have an active AlgoBuilder account with this email. To switch to the free plan as a Switch&nbsp;Markets trader,{" "}
            <a
              href={WEBSITE_URL + "contact-us"}
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              please contact support.
            </a>
          </span>
        )
      } else if (code === "SPONSOR_ELIGIBILITY_ERROR") {
        setFormError(err.userMessage || "Eligibility check failed.")
      } else if (code === "TURNSTILE_REQUIRED") {
        setFormError("Please complete the security verification.")
      } else if (code === "TURNSTILE_INVALID") {
        setFormError("Security verification failed. Please try again.")
      } else {
        setFormError("An error occurred. Please try again.")
      }
    }
  }

  const captchaSlot = (
    <div className="flex flex-col gap-2">
      {formError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-caption text-destructive">
          {formError}
        </div>
      )}
      <TurnstileWidget
        siteKey={TURNSTILE_SITE_KEY}
        onToken={(token) => {
          setTurnstileToken(token)
          setCaptchaReady(true)
        }}
        onExpire={() => {
          setTurnstileToken("")
          setCaptchaReady(false)
        }}
        theme={isDark ? "dark" : "light"}
        appearance="interaction-only"
      />
    </div>
  )

  const brandSlot = (
    <div className="flex flex-col items-center gap-2">
      <a href="https://algobuilder.com">
        <BrandLogo height={22} theme={isDark ? "dark" : "light"} />
      </a>
      <p className="text-caption text-muted-foreground flex items-center gap-2">
        <span>in partnership with</span>
        <SwitchMarketsLogo />
      </p>
    </div>
  )

  const footerSlot = (
    <>&copy; {new Date().getFullYear()} AlgoBuilder. All rights reserved.</>
  )

  if (success) {
    return (
      <AuthLayout brandSlot={brandSlot} footerSlot={footerSlot}>
        <div className="text-center py-4">
          <CheckCircle
            size={36}
            strokeWidth={1.2}
            className="text-primary mx-auto mb-3"
          />
          <h1 className="text-page-title text-foreground mb-1.5">
            You&apos;re all set!
          </h1>
          <p className="text-body text-muted-foreground leading-relaxed max-w-[280px] mx-auto mb-4">
            Welcome to AlgoBuilder. Your free access as a Switch&nbsp;Markets trader has been activated. Redirecting you to your Dashboard in 5 seconds...
          </p>
          <Button
            className="w-full text-label"
            onClick={() => navigate("/")}
          >
            Go to Dashboard Now
          </Button>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout brandSlot={brandSlot} footerSlot={footerSlot}>
      <SignUpForm
        heading="Open Account"
        description="AlgoBuilder is free for all funded traders at Switch Markets."
        defaultEmail={searchParams.get("email") || ""}
        loading={loading || !captchaReady}
        submittingLabel={loading ? "Creating account..." : "Verifying you're human..."}
        errors={errors}
        submitLabel="Register"
        captchaSlot={captchaSlot}
        termsLabel={
          <>
            I have read and accept the{" "}
            <a
              href="https://algobuilder.com/terms-and-conditions/"
              className="text-primary hover:text-primary/80 no-underline transition-colors"
            >
              Terms &amp; Conditions
            </a>
            . See our{" "}
            <a
              href="https://algobuilder.com/privacy-policy/"
              className="text-primary hover:text-primary/80 no-underline transition-colors"
            >
              Privacy Policy
            </a>
            .
          </>
        }
        signInPrompt="Already have an account?"
        signInLinkSlot={
          <Link
            to="/sign/in"
            className="text-primary hover:text-primary/80 no-underline font-semibold transition-colors"
          >
            Login
          </Link>
        }
        onSubmit={handleSubmit}
      />
    </AuthLayout>
  )
}
